/* Mushy · Rail
   Ein Slider-Motor fuer alles, was sich seitlich schieben laesst: Stimmen,
   Produktbilder, Bundle-Bilder, Pilzkarten, Marke. Die Spur ist ein
   normaler Scroll-Container mit Scroll-Snap — Finger und Trackpad wischen
   nativ, das Script ergaenzt nur, was der Browser nicht mitbringt:

     · Maus-Drag (Desktop hat keinen Finger)
     · Pfeile und Punkte
     · den aktiven Index, damit Section-Code darauf reagieren kann
     · Autoplay, das beim Lesen pausiert

   Markup:
     <mu-rail data-snap="center|start" data-loop="true" data-autoplay="true" data-interval="7">
       <div data-rail-track> …Kinder = Slides… </div>
       <button data-rail-prev> · <button data-rail-next> · <div data-rail-dots>
     </mu-rail>

   Die Regeln fuer Drag und Snap stammen aus dem Jello-Projekt (LEARNINGS.md
   dort §3 und §4): Pointer-Capture erst nach der Achsen-Entscheidung, Snap
   waehrend des Zugs aus, Klick nach Zug schlucken, Loslassen ausserhalb
   ueber window abfangen. */

if (!customElements.get('mu-rail')) {
  customElements.define(
    'mu-rail',
    class MuRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rail-track]');
        if (!this.track) return;

        this.items = Array.from(this.track.children);
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        this.dotsBox = this.querySelector('[data-rail-dots]');
        this.snap = this.dataset.snap === 'center' ? 'center' : 'start';
        this.loop = this.dataset.loop === 'true';
        // Passiv: nur ziehen und scrollen, keinen aktiven Slide fuehren —
        // fuer Thumb-Leisten, deren "aktiv" etwas anderes bedeutet.
        this.passive = this.dataset.passive === 'true';
        this.index = 0;

        this.buildDots();
        this.bindNav();
        this.bindDrag();
        this.bindScroll();
        this.bindAutoplay();
        this.paint();

        // Beim Skalieren die aktive Karte an ihrem Platz halten.
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => this.go(this.index, false));
          this.ro.observe(this.track);
        }
      }

      disconnectedCallback() {
        this.stop();
        if (this.ro) this.ro.disconnect();
      }

      /* ───────── Geometrie ───────── */

      // Ziel-scrollLeft, damit Slide i am Snap-Punkt steht.
      targetFor(i) {
        const el = this.items[i];
        if (!el) return 0;
        const left = el.offsetLeft - this.track.offsetLeft;
        if (this.snap === 'center') {
          return left - (this.track.clientWidth - el.offsetWidth) / 2;
        }
        return left;
      }

      // Welcher Slide steht gerade am Snap-Punkt?
      nearest() {
        const x = this.track.scrollLeft;
        let best = 0;
        let dist = Infinity;
        this.items.forEach((el, i) => {
          const d = Math.abs(this.targetFor(i) - x);
          if (d < dist) { dist = d; best = i; }
        });
        return best;
      }

      /* Wo landet ein Zug?
         Vorher entschied allein die Naehe: unter einer halben Bildbreite
         sprang die Spur zurueck. Ein kurzer, zuegiger Zug — also die Geste,
         mit der jeder zum naechsten Bild will — endete damit immer wieder
         auf demselben Bild. Jetzt zaehlen Richtung, Weg und Tempo:
         ein Fuenftel Bildbreite oder ein schneller Zug reichen. */
      landing(from, moved, speed) {
        const width = (this.items[0] && this.items[0].offsetWidth) || this.track.clientWidth || 1;
        if (this.passive) return this.nearest();

        const dir = moved === 0 ? 0 : (moved > 0 ? 1 : -1);
        if (!dir) return from;

        const abs = Math.abs(moved);
        let steps = Math.floor(abs / width);
        const rest = abs - steps * width;
        const flick = dir === 1 ? speed < -0.3 : speed > 0.3;

        if (rest > width * 0.2 || (flick && steps === 0)) steps += 1;
        return from + dir * steps;
      }

      go(i, smooth = true) {
        const n = this.items.length;
        if (!n) return;
        if (this.loop) i = (i + n) % n;
        else i = Math.max(0, Math.min(n - 1, i));

        this.index = i;
        this.paint();
        this.track.scrollTo({
          left: this.targetFor(i),
          behavior: smooth && !this.reduced() ? 'smooth' : 'instant'
        });
      }

      reduced() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /* ───────── Bedienung ───────── */

      bindNav() {
        if (this.prev) this.prev.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
        if (this.next) this.next.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
      }

      buildDots() {
        if (!this.dotsBox || this.items.length < 2) return;
        if (!this.dotsBox.children.length) {
          this.items.forEach((_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'mu-rail__dot';
            b.setAttribute('aria-label', `Bild ${i + 1}`);
            this.dotsBox.appendChild(b);
          });
        }
        this.dots = Array.from(this.dotsBox.children);
        this.dots.forEach((d, i) => d.addEventListener('click', () => { this.go(i); this.restart(); }));
      }

      paint() {
        if (this.passive) return;
        this.items.forEach((el, i) => el.classList.toggle('is-active', i === this.index));
        if (this.dots) {
          this.dots.forEach((d, i) => {
            d.classList.toggle('is-active', i === this.index);
            d.setAttribute('aria-current', i === this.index ? 'true' : 'false');
          });
        }
        if (!this.loop) {
          if (this.prev) this.prev.disabled = this.index === 0;
          if (this.next) this.next.disabled = this.index === this.items.length - 1;
        }
        this.dispatchEvent(new CustomEvent('mu:rail-change', {
          bubbles: true,
          detail: { index: this.index }
        }));
      }

      // Index aus der Scroll-Position lesen — auch nach Finger-Wischen.
      bindScroll() {
        let raf = 0;
        if (this.passive) return;
        this.track.addEventListener('scroll', () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            const i = this.nearest();
            if (i !== this.index) { this.index = i; this.paint(); }
          });
        }, { passive: true });
      }

      /* ───────── Maus-Drag ─────────
         Nur fuer die Maus; Finger und Stift scrollen nativ. */
      bindDrag() {
        const t = this.track;
        let down = false, dragging = false, startX = 0, startY = 0, startLeft = 0, id = null;
        let startIndex = 0, lastX = 0, lastT = 0, speed = 0;

        const end = () => {
          if (!down) return;
          down = false;
          if (id !== null && t.hasPointerCapture && t.hasPointerCapture(id)) t.releasePointerCapture(id);
          id = null;
          if (dragging) {
            dragging = false;
            // Nach dem Zug einrasten lassen: Snap kommt zurueck, sobald die
            // Ruhe eintritt — vorher wuerde er die weiche Bewegung killen.
            this.go(this.landing(startIndex, t.scrollLeft - startLeft, speed));
            setTimeout(() => t.classList.remove('is-dragging'), 450);
            // Der Klick nach einem Zug ist keiner.
            this.swallow = true;
            setTimeout(() => { this.swallow = false; }, 0);
          }
        };

        t.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          down = true; dragging = false;
          startX = e.clientX; startY = e.clientY; startLeft = t.scrollLeft; id = e.pointerId;
          startIndex = this.index; lastX = e.clientX; lastT = e.timeStamp; speed = 0;
          this.stop();
        });

        t.addEventListener('pointermove', (e) => {
          if (!down || e.pointerType !== 'mouse') return;
          // Maus ohne gedrueckte Taste: das Loslassen kam nie an.
          if (!(e.buttons & 1)) { end(); return; }
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (!dragging) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            if (Math.abs(dy) > Math.abs(dx)) { down = false; return; }
            dragging = true;
            t.classList.add('is-dragging');
            if (t.setPointerCapture) t.setPointerCapture(id);
          }
          // Tempo aus den letzten beiden Punkten, in px pro ms.
          const dt = e.timeStamp - lastT;
          if (dt > 0) speed = (e.clientX - lastX) / dt;
          lastX = e.clientX; lastT = e.timeStamp;

          t.scrollLeft = startLeft - dx;
          e.preventDefault();
        });

        ['pointerup', 'pointercancel', 'blur'].forEach((ev) => window.addEventListener(ev, end));

        t.addEventListener('click', (e) => {
          if (this.swallow) { e.stopPropagation(); e.preventDefault(); }
        }, true);

        t.addEventListener('dragstart', (e) => e.preventDefault());

        /* Scroll-Falle: ueber einer waagerecht scrollbaren Spur rastet Chrome
           eine leicht schraege Geste auf die Waagerechte ein. Die Seite steht
           dann still und nur die Karten wandern. Ist die Geste ueberwiegend
           senkrecht, geben wir sie an die Seite zurueck. */
        t.addEventListener(
          'wheel',
          (e) => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
            e.preventDefault();
            window.scrollBy({ top: e.deltaY, behavior: 'instant' });
          },
          { passive: false }
        );
      }

      /* ───────── Autoplay ───────── */

      bindAutoplay() {
        if (this.dataset.autoplay !== 'true' || this.items.length < 2) return;
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.restart());
        this.addEventListener('focusin', () => this.stop());
        this.track.addEventListener('touchstart', () => this.stop(), { passive: true });
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.stop(); else this.restart();
        });
        this.restart();
      }

      stop() { clearInterval(this.timer); }

      restart() {
        this.stop();
        if (this.dataset.autoplay !== 'true') return;
        const s = Math.max(3, parseInt(this.dataset.interval, 10) || 7);
        this.timer = setInterval(() => this.go(this.index + 1), s * 1000);
      }
    }
  );
}
